import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Collapse,
  Divider,
  Row,
  Select,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import type { AnalyticsPeriod } from '@/components';
import { InstallSourceTable } from '@/components/InstallSourceTable';
import type { PlayerCohort } from '@/types';
import type { InstallSourceReport } from '@/types/installSource';
import { apiErrorMessage } from '@utils/apiError';

const { Title, Text } = Typography;

// Moved here from the Dashboard (removed 26 Oct 2026). Each panel loads only when it is first opened, so the
// player list itself opens as fast as before; the reports take 2-6 s each on production.

interface RetentionFunnel {
  totalRegistered: number;
  dau: number;
  wau: number;
  mau: number;
  atRisk_7_30d: number;
  likelyChurned_30d_plus: number;
  neverLoggedInAgainAfterRegister: number;
}

interface FunnelDropOff {
  usersWithZeroMatches: number;
  buckets: { registeredLast24h: number; registeredLast7d: number; registeredOver7dAgo: number };
  loggedInPattern: {
    loggedInLast24h: number;
    loggedIn_2_to_7_days: number;
    loggedIn_8_to_30_days: number;
    loggedInOver30DaysAgo: number;
    neverLoggedInAgain: number;
  };
  ftueStateBreakdown: { ftueCompleted: number; ftueIncomplete: number; ftueStateUnknown: number };
}

/** Loads a report on mount (the panel's first open) and on Retry. */
function useReport<T>(load: () => Promise<{ data: T }>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const run = useCallback(() => {
    let live = true;
    setLoading(true);
    setError(null);
    load()
      .then((res) => live && setData(res.data))
      .catch((err) => live && setError(apiErrorMessage(err, 'Failed to load this report.')))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [load]);
  useEffect(() => run(), [run]);
  return { data, error, loading, retry: run };
}

const ReportState: React.FC<{ error: string | null; loading: boolean; retry: () => void }> = ({
  error,
  loading,
  retry,
}) =>
  error ? (
    <Alert
      type="warning"
      showIcon
      message={error}
      action={
        <Button size="small" icon={<ReloadOutlined />} onClick={retry}>
          Retry
        </Button>
      }
    />
  ) : loading ? (
    <div style={{ textAlign: 'center', padding: 24 }}>
      <Spin />
    </div>
  ) : null;

/** A count that, when clicked, filters the list below to that cohort. */
const CohortStat: React.FC<{
  title: string;
  value: number;
  color?: string;
  cohort: PlayerCohort;
  onCohort: (cohort: PlayerCohort) => void;
}> = ({ title, value, color, cohort, onCohort }) => (
  <Button
    type="text"
    style={{ height: 'auto', padding: 0, textAlign: 'left' }}
    onClick={() => onCohort(cohort)}
  >
    <Statistic title={`${title} · show`} value={value} valueStyle={color ? { color } : undefined} />
  </Button>
);

const RetentionPanel: React.FC<{ onCohort: (cohort: PlayerCohort) => void }> = ({ onCohort }) => {
  const load = useCallback(
    () => adminApi.getRetentionFunnel() as Promise<{ data: RetentionFunnel }>,
    []
  );
  const { data, error, loading, retry } = useReport(load);
  if (!data) return <ReportState error={error} loading={loading} retry={retry} />;
  return (
    <Row gutter={[16, 16]}>
      <Col xs={12} md={6} lg={4}>
        <Statistic title="DAU (today)" value={data.dau} />
      </Col>
      <Col xs={12} md={6} lg={4}>
        <Statistic title="WAU (7d)" value={data.wau} />
      </Col>
      <Col xs={12} md={6} lg={4}>
        <Statistic title="MAU (30d)" value={data.mau} />
      </Col>
      <Col xs={12} md={6} lg={6}>
        <CohortStat
          title="At-risk (7-30d)"
          value={data.atRisk_7_30d}
          color="#fa8c16"
          cohort="at-risk"
          onCohort={onCohort}
        />
      </Col>
      <Col xs={12} md={6} lg={6}>
        <CohortStat
          title="Churned (>30d)"
          value={data.likelyChurned_30d_plus}
          color="#ff4d4f"
          cohort="churned"
          onCohort={onCohort}
        />
      </Col>
    </Row>
  );
};

const NoMatchPanel: React.FC<{ onCohort: (cohort: PlayerCohort) => void }> = ({ onCohort }) => {
  const load = useCallback(
    () => adminApi.getFunnelDropOff() as Promise<{ data: FunnelDropOff }>,
    []
  );
  const { data, error, loading, retry } = useReport(load);
  if (!data) return <ReportState error={error} loading={loading} retry={retry} />;
  const divider = <Divider style={{ margin: '8px 0' }} />;
  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Title level={5}>Registered when</Title>
          <Statistic title="Last 24h" value={data.buckets.registeredLast24h} />
          {divider}
          <Statistic title="Last 7 days" value={data.buckets.registeredLast7d} />
          {divider}
          <Statistic
            title=">7 days ago"
            value={data.buckets.registeredOver7dAgo}
            valueStyle={{ color: '#ff4d4f' }}
          />
        </Col>
        <Col xs={24} md={8}>
          <Title level={5}>Last-login pattern</Title>
          <Statistic title="Today" value={data.loggedInPattern.loggedInLast24h} />
          {divider}
          <Statistic title="2-7 days" value={data.loggedInPattern.loggedIn_2_to_7_days} />
          {divider}
          <Statistic title="8-30 days" value={data.loggedInPattern.loggedIn_8_to_30_days} />
          {divider}
          <Statistic
            title=">30 days / never"
            value={
              data.loggedInPattern.loggedInOver30DaysAgo + data.loggedInPattern.neverLoggedInAgain
            }
            valueStyle={{ color: '#ff4d4f' }}
          />
        </Col>
        <Col xs={24} md={8}>
          <Title level={5}>FTUE state</Title>
          <Statistic
            title="Completed FTUE"
            value={data.ftueStateBreakdown.ftueCompleted}
            valueStyle={{ color: '#52c41a' }}
          />
          {divider}
          <Statistic
            title="Stuck in FTUE"
            value={data.ftueStateBreakdown.ftueIncomplete}
            valueStyle={{ color: '#fa8c16' }}
          />
          {divider}
          <Statistic title="Unknown" value={data.ftueStateBreakdown.ftueStateUnknown} />
        </Col>
      </Row>
      <Divider />
      <Button onClick={() => onCohort('no-matches')}>
        Show the {data.usersWithZeroMatches.toLocaleString()} players with no matches
      </Button>
    </>
  );
};

const PERIODS: Array<{ value: AnalyticsPeriod; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
];

const InstallSourcePanel: React.FC = () => {
  const [period, setPeriod] = useState<AnalyticsPeriod>('last_7_days');
  const load = useCallback(
    () => adminApi.getInstallSources({ period }) as Promise<{ data: InstallSourceReport }>,
    [period]
  );
  const { data, error, loading, retry } = useReport(load);
  return (
    <>
      <Select
        size="small"
        value={period}
        onChange={setPeriod}
        options={PERIODS}
        style={{ width: 140, marginBottom: 12 }}
      />
      {data && !loading ? (
        <InstallSourceTable report={data} />
      ) : (
        <ReportState error={error} loading={loading} retry={retry} />
      )}
    </>
  );
};

/** Player activity, no-match players and install source, above the player list. */
export const PlayerSummaryPanels: React.FC<{ onCohort: (cohort: PlayerCohort) => void }> = ({
  onCohort,
}) => (
  <Collapse
    style={{ marginBottom: 16 }}
    items={[
      {
        key: 'retention',
        label: (
          <span>
            Player activity <Text type="secondary">DAU / WAU / MAU, at-risk and churned</Text>
          </span>
        ),
        children: <RetentionPanel onCohort={onCohort} />,
      },
      {
        key: 'no-matches',
        label: (
          <span>
            Players with no matches{' '}
            <Text type="secondary">when they registered, last login, FTUE state</Text>
          </span>
        ),
        children: <NoMatchPanel onCohort={onCohort} />,
      },
      {
        key: 'install-source',
        label: (
          <span>
            Install source <Tag>AppsFlyer</Tag>
            <Text type="secondary">where new players came from</Text>
          </span>
        ),
        children: <InstallSourcePanel />,
      },
    ]}
  />
);
